import React, { useState, useEffect, useCallback } from 'react';
import useViewerIdentity from '../../hooks/useViewerIdentity';
import useCanWrite from '../../hooks/useCanWrite';
import { subscribeToAuthEvents, AUTH_EVENT_TYPES } from '../../utils/authEvents';
import './ModerationBanner.css';

/**
 * Global moderation banner that:
 * 1. Shows a persistent banner at the top of the page if the current user is
 *    muted/suspended/banned, explaining their status.
 * 2. Shows a transient toast when a 403 Forbidden error occurs (e.g. the user
 *    tried to comment but is muted), so they get immediate feedback instead
 *    of being silently logged out.
 */
const ModerationBanner = () => {
  const { user } = useViewerIdentity();
  const { canWrite, status, reason } = useCanWrite();
  const [toast, setToast] = useState(null);

  const handleAuthEvent = useCallback((event) => {
    if (event?.type === AUTH_EVENT_TYPES.FORBIDDEN) {
      setToast({
        message: event.payload?.reason || 'You do not have permission to perform this action.',
        timestamp: Date.now(),
      });
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents(handleAuthEvent);
    return unsubscribe;
  }, [handleAuthEvent]);

  // Auto-dismiss toast after 6 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Don't show anything if user is not logged in or can write and no toast
  if (!user) return null;

  return (
    <>
      {/* Persistent status banner for muted/suspended/banned users */}
      {!canWrite && (
        <div className={`mod-banner mod-banner--${(status || 'muted').toLowerCase()}`}>
          <div className="mod-banner__content">
            <span className="mod-banner__icon">
              {status === 'BANNED' ? '⛔' : status === 'SUSPENDED' ? '⏸' : '🔇'}
            </span>
            <span className="mod-banner__text">{reason}</span>
          </div>
        </div>
      )}

      {/* Transient toast for 403 errors */}
      {toast && (
        <div className="mod-toast" key={toast.timestamp}>
          <div className="mod-toast__content">
            <span className="mod-toast__icon">⚠</span>
            <span className="mod-toast__text">{toast.message}</span>
            <button className="mod-toast__close" onClick={() => setToast(null)}>×</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ModerationBanner;
